import { useState, useEffect } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Package,
  Building
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, query, orderBy, getDoc, addDoc } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/lib/firebase';
import { Product, Laboratory, ProductCategory } from '@/types/product';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Helmet } from 'react-helmet-async';

const categories: { value: ProductCategory; label: string }[] = [
  { value: 'medicament', label: 'Médicament' },
  { value: 'complement', label: 'Complément alimentaire' },
  { value: 'beaute', label: 'Produit de beauté' },
  { value: 'materiel', label: 'Matériel médical' },
];

function ProductForm({ laboratories, onSuccess }: { laboratories: Laboratory[], onSuccess: () => void }) {
  const [form, setForm] = useState<Omit<Product, 'id'|'createdAt'|'updatedAt'>>({
    name: '',
    description: '',
    category: 'medicament',
    laboratoryId: laboratories[0]?.id || '',
    images: [],
    price: '',
    dosage: '',
    presentation: '',
    indication: '',
    composition: '',
    status: 'active',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleCategoryChange = (value: ProductCategory) => {
    setForm({ ...form, category: value });
  };
  const handleLabChange = (value: string) => {
    setForm({ ...form, laboratoryId: value });
  };
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    let imageUrl = form.images[0] || '';
    if (imageFile) {
      const storageRef = (await import('firebase/storage')).ref;
      const { uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = (await import('@/lib/firebase')).storage;
      const fileRef = storageRef(storage, `products/${Date.now()}_${imageFile.name}`);
      const snapshot = await uploadBytes(fileRef, imageFile);
      imageUrl = await getDownloadURL(snapshot.ref);
    }
    const productData = {
      ...form,
      images: [imageUrl],
      status: form.status,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
    setForm({ name: '', description: '', category: 'medicament', laboratoryId: laboratories[0]?.id || '', images: [], price: '', dosage: '', presentation: '', indication: '', composition: '', status: 'active' });
    setImageFile(null);
    setLoading(false);
    onSuccess();
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Nom du produit</Label>
          <Input name="name" value={form.name} onChange={handleInputChange} required />
        </div>
        <div>
          <Label>Type d'usage</Label>
          <Select value={form.category} onValueChange={handleCategoryChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Laboratoire</Label>
          <Select value={form.laboratoryId} onValueChange={handleLabChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {laboratories.map(lab => (
                <SelectItem key={lab.id} value={lab.id}>{lab.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <textarea name="description" value={form.description} onChange={handleInputChange} className="w-full border rounded p-2" required />
      </div>
      <div>
        <Label>Image</Label>
        <Input type="file" accept="image/*" onChange={handleImageChange} />
        {form.images[0] && !imageFile && (
          <img src={form.images[0]} alt="produit" className="h-16 mt-2" />
        )}
        {imageFile && (
          <span className="text-sm text-gray-500">{imageFile.name}</span>
        )}
      </div>
      <div className="flex gap-4">
        <Button type="submit" disabled={loading}>Ajouter</Button>
      </div>
    </form>
  );
}

const AdminProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedLabId, setSelectedLabId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsSnapshot, laboratoriesSnapshot] = await Promise.all([
        getDocs(query(collection(db, COLLECTIONS.PRODUCTS), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, COLLECTIONS.LABORATORIES))
      ]);

      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];

      const laboratoriesData = laboratoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Laboratory[];

      setProducts(productsData);
      setLaboratories(laboratoriesData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, id));
      setProducts(products.filter(item => item.id !== id));
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const getLaboratoryName = (laboratoryId: string) => {
    const laboratory = laboratories.find(lab => lab.id === laboratoryId);
    return laboratory?.name || 'Laboratoire inconnu';
  };

  const filteredProducts = products.filter(item => {
    const laboratoryName = getLaboratoryName(item.laboratoryId);
    const matchesLab = selectedLabId ? item.laboratoryId === selectedLabId : true;
    return (
      matchesLab && (
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        laboratoryName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  });

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion des produits | LeaderProMed Admin</title>
        <meta name="description" content="Interface d'administration pour la gestion des produits médicaux, pharmaceutiques et dispositifs en Mauritanie et Afrique du Nord." />
        <meta property="og:title" content="Gestion des produits | LeaderProMed Admin" />
        <meta property="og:description" content="Interface d'administration pour la gestion des produits médicaux, pharmaceutiques et dispositifs en Mauritanie et Afrique du Nord." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="flex h-screen bg-gray-100">
        <AdminSidebar />
        
        <div className="flex-1 overflow-auto">
          <div className="p-8">
            {/* Cartes laboratoires en grille 6 colonnes */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
              {laboratories.map(lab => (
                <Card
                  key={lab.id}
                  className={`flex flex-col items-center p-4 hover:shadow-md transition-shadow cursor-pointer ${selectedLabId === lab.id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setSelectedLabId(lab.id)}
                >
                  <img src={lab.logo} alt={lab.name} className="h-12 w-12 object-contain rounded bg-gray-100 mb-2" />
                  <CardTitle className="text-base text-center mb-1">{lab.name}</CardTitle>
                  <div className="text-xs text-gray-500 text-center">{lab.country}</div>
                </Card>
              ))}
            </div>
            {selectedLabId && (
              <div className="mb-4 flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setSelectedLabId(null)}>
                  Afficher tous les produits
                </Button>
              </div>
            )}
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Gestion des Produits</h1>
                <p className="text-gray-600">Gérez votre catalogue de produits</p>
              </div>
              <div className="flex gap-2">
                <Link to="/admin/laboratories">
                  <Button variant="secondary" className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Nouveau laboratoire
                  </Button>
                </Link>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Nouveau produit
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Ajouter un produit</DialogTitle>
                    </DialogHeader>
                    <ProductForm laboratories={laboratories} onSuccess={() => { setOpen(false); fetchData(); }} />
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Barre de recherche */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher dans les produits..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Liste des produits */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredProducts.map((item) => (
                <Card key={item.id} className="flex flex-col items-center p-4 hover:shadow-md transition-shadow">
                  <img
                    src={item.images[0] || '/public/images/products/default-product.png'}
                    alt={item.name}
                    className="h-16 w-16 object-contain rounded bg-gray-100 mb-2"
                  />
                  <CardTitle className="text-base text-center mb-1">{item.name}</CardTitle>
                  <div className="text-xs text-blue-600 mb-1">{categories.find(cat => cat.value === item.category)?.label}</div>
                  <div className="text-xs text-gray-500 text-center mb-2">{getLaboratoryName(item.laboratoryId)}</div>
                  <div className="flex gap-2 mt-auto">
                    <Link to={`/admin/products/edit/${item.id}`}>
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                          <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(item.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </Card>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucun produit trouvé</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminProducts; 